import React, { createContext, Suspense, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { isMobile } from './utils/isMobile';
import { getAppName } from './utils/DownloadUtils';
import ShareRecorded from './pages/share_recorded';
import AboutApp from './pages/about_app';
import PriceUp from './pages/price_up';
import SpaceUpgrade from './pages/space_upgrade';
import ShareWord from './pages/share_word';
import ExpandSpace from './pages/expand_space';
import { JsBridge } from './utils/JsBridge';
import { isApp } from './utils/EnvUtils';
import { DeerUser } from './typings/DeerUser';
import ResourcesList from './pages/photo_generate_book/ResourcesList';

const H5Website = React.lazy(() => import('./pages/h5_website'));
const Website = React.lazy(() => import('./pages/website'));
const XsmH5Website = React.lazy(() => import('./pages/xsm/h5_website'));
const XsmWebsite = React.lazy(() => import('./pages/xsm/website'));
const H5SelfService = React.lazy(() => import('./pages/h5_self_service'));
const VipSale = React.lazy(() => import('./pages/h5_vip_sale'));
const BaiduPan = React.lazy(() => import('./pages/h5_self_service_baidu'));
const ShareCourse = React.lazy(() => import('./pages/share_course'));
const ShareCourseOutside = React.lazy(
  () => import('./pages/share_course_outside')
);
const VipShare = React.lazy(() => import('./pages/vip_share/index'));
const CumulativeInvitation = React.lazy(
  () => import('./pages/cumulative_invitation/index')
);
const AccountDetails = React.lazy(() => import('./pages/account_details'));
const ActivityDescription = React.lazy(
  () => import('./pages/activity_description')
);
const DailyPaper = React.lazy(() => import('./pages/daily_paper'));
const LearningReportDetail = React.lazy(
  () => import('./pages/learning_report_detail')
);
const VipShareActivity = React.lazy(() => import('./pages/vip_share_activity'));
const GreatTracher = React.lazy(() => import('./pages/great_teacher'));
const FormTeacher = React.lazy(() => import('./pages/form_teacher'));
const NYSpecial = React.lazy(() => import('./pages/ny_special'));
const GenerateBookCreate = React.lazy(
  () => import('./pages/photo_generate_book/CreateOrEditPage')
);
const CreatorCenter = React.lazy(() => import('./pages/creator'));
const EditWords = React.lazy(() => import('./pages/edit_words'));
const CooperativePromotion = React.lazy(
  () => import('./pages/cooperative_promotion')
);
const LiveClassPromotion = React.lazy(
  () => import('./pages/live_class_promotion')
);
const CocoLiveClass = React.lazy(
  () => import('./pages/live_class_promotion/CocoLiveClass')
);
const CocoGrammarCourse = React.lazy(
  () => import('./pages/live_class_promotion/CocoGrammarCourse')
);
const Disclaimer = React.lazy(() => import('./pages/disclaimer'));
const VideoPlayPage = React.lazy(
  () => import('./pages/live_class_promotion/VideoPlayPage')
);
const PhoneticPage = React.lazy(() => import('./pages/phonetic'));
const LetterPhoneticPage = React.lazy(() => import('./pages/phonetic/letter'));
const PhonicsPage = React.lazy(() => import('./pages/phonics'));
const AiChat = React.lazy(() => import('./pages/ai_chat'));
const Leaderboard = React.lazy(() => import('./pages/leaderboard'));
const MockTestReportLanding = React.lazy(
  () => import('./pages/mock_test_report_landing')
);
const MindmapPage = React.lazy(() => import('./pages/mindmap'));
const MyGroupPurchase = React.lazy(() => import('./pages/my_group_purchase'));
const JoinGroupPurchase = React.lazy(
  () => import('./pages/join_group_purchase')
);
const GroupPurchaseShareActivity = React.lazy(
  () => import('./pages/group_purchase_share_activity')
);

/** 业务类型：主站 / 小四门 / sno，后续可扩展其它 webtype */
export type WebType = 'main' | 'xsm' | 'sno';

/** 根据 URL 参数 webtype 或域名解析当前业务类型，便于扩展多业务（与 public/meta-by-host.js 保持一致） */
const getWebType = (): WebType => {
  if (typeof window === 'undefined') return 'main';
  const params = new URLSearchParams(window.location.search);
  const webtype = params.get('webtype');
  if (webtype === 'sno') return 'sno';
  if (webtype === 'xsm') return 'xsm';
  const host = window.location.hostname;
  if (
    host === 'sno.sduck.net' ||
    host === 'snotest.sduck.net' ||
    host.endsWith('.sno.sduck.net') ||
    host.endsWith('.snotest.sduck.net')
  ) {
    return 'sno';
  }
  if (
    host === 'xsm.sduck.net' ||
    host === 'xsmtest.sduck.net' ||
    host.endsWith('.xsm.sduck.net') ||
    host.endsWith('.xsmtest.sduck.net')
  ) {
    return 'xsm';
  }
  return 'main';
};

/** 根据域名 / webtype 切换 favicon（需在 public 下放置 favicon-xsm.ico、favicon-sno.ico） */
function setFaviconByDomain() {
  if (typeof window === 'undefined') return;
  const appName = getAppName();
  if (!appName) return;
  const link = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
  if (link) {
    const base = process.env.PUBLIC_URL || '';
    link.href = `${base}/favicon-${appName}.ico`;
  }
}
setFaviconByDomain();

/** 根据 webtype 注入 JSON-LD 结构化数据 */
function setJsonLdByWebType() {
  if (typeof document === 'undefined') return;

  const webtype = getWebType();

  const mainJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'EducationalApplication',
    name: '天才鸭背单词',
    description:
      '上海进步少年科技打造的一站式专业英语学习平台，支持英音/美音双音切换、AI跟读逐音标打分、智能拼读音节拆分，覆盖少儿英语启蒙、成人发音矫正、雅思托福备考，实现个性化分层学习。',
    applicationCategory: 'LanguageLearningApplication',
    operatingSystem: 'Web',
    url: 'https://sduck.net/',
    featureList: [
      '英音美音双音切换',
      '48个国际音标教学',
      'AI智能跟读纠音',
      '单词音节智能拆分',
      '自然拼读体系',
    ],
    developer: {
      '@type': 'Corporation',
      name: '上海进步少年科技发展有限公司',
      url: 'https://sduck.net/',
    },
    license: '沪ICP备2024107815号-1',
  };

  const xsmJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'EducationalApplication',
    name: '题霸优学',
    description:
      '一站式专业初中小四门学习平台，聚焦历史、地理、生物、道法学科，提供课本同步刷题、核心考点速记、高频真题测验、思维导图知识总结，智能适配学段，助力孩子高效攻克小四门。',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web',
    url: 'https://sduck.net/?webtype=xsm',
    featureList: [
      '课本同步在线刷题',
      '核心考点速记提纲',
      '历年高频考点测验',
      '思维导图知识总结',
      '学段智能推荐学习',
    ],
    developer: {
      '@type': 'Corporation',
      name: '上海进步少年科技发展有限公司',
      url: 'https://sduck.net/',
    },
    license: '沪ICP备2024107815号-1',
  };

  const snoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'EducationalApplication',
    name: 'AI口语私教1v1',
    description:
      '主打 AI 口语私教一对一：智能对话陪练、实时发音反馈与情景化演练，模拟真实交流节奏，个性化推荐练习内容，让口语输出更自信、更地道。',
    applicationCategory: 'LanguageLearningApplication',
    operatingSystem: 'Web',
    url: 'https://sduck.net/?webtype=sno',
    featureList: [
      'AI 一对一口语陪练',
      '实时发音与语调反馈',
      '多情景对话演练',
      '个性化学习路径',
      '沉浸式口语输出训练',
    ],
    developer: {
      '@type': 'Corporation',
      name: '上海进步少年科技发展有限公司',
      url: 'https://sduck.net/',
    },
    license: '沪ICP备2024107815号-1',
  };

  const data =
    webtype === 'xsm'
      ? xsmJsonLd
      : webtype === 'sno'
        ? snoJsonLd
        : mainJsonLd;

  const head = document.head || document.getElementsByTagName('head')[0];
  if (!head) return;

  const existing = document.getElementById('app-structured-data');
  if (existing && existing.parentNode) {
    existing.parentNode.removeChild(existing);
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = 'app-structured-data';
  script.text = JSON.stringify(data);
  head.appendChild(script);
}
setJsonLdByWebType();

/** 根路径根据 webtype 渲染不同主页：main 主站，xsm 小四门，后续可扩展 */
const HomeByDomain = () => {
  const webtype = getWebType();
  if (webtype === 'xsm') {
    return isMobile() ? <XsmH5Website /> : <XsmWebsite />;
  }
  return isMobile() ? <H5Website /> : <Website />;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <HomeByDomain />,
  },
  {
    path: '/wechat',
    element: <HomeByDomain />,
  },
  {
    path: '/app',
    element: <HomeByDomain />,
  },
  {
    path: '/self_service',
    element: <H5SelfService />,
  },
  {
    path: '/self_service_baidu',
    element: <BaiduPan />,
  },
  {
    path: '/share_course', // 内部分享页
    element: <ShareCourse />,
  },
  {
    path: '/share_course_outside', // 外部分享落地页
    element: <ShareCourseOutside />,
  },
  {
    path: '/vip_sale',
    element: <VipSale />,
  },
  {
    path: '/vip_share',
    element: <VipShare />,
  },
  {
    path: '/vip_share_activity',
    element: <VipShareActivity />,
  },
  {
    path: '/cumulative_invitation',
    element: <CumulativeInvitation />,
  },
  {
    path: '/account_details',
    element: <AccountDetails />,
  },
  {
    path: '/activity_description',
    element: <ActivityDescription />,
  },
  {
    path: '/recorded_share',
    element: <ShareRecorded />,
  },
  {
    path: '/about_app',
    element: <AboutApp />,
  },
  {
    path: '/great_teacher',
    element: <FormTeacher />,
  },
  {
    path: '/form_teacher',
    element: <FormTeacher />,
  },
  {
    path: '/price_up',
    element: <PriceUp />,
  },
  {
    path: '/daily_paper',
    element: <DailyPaper />,
  },
  {
    path: 'learning_report_detail',
    element: <LearningReportDetail />,
  },
  {
    path: '/ny_special',
    element: <NYSpecial />,
  },
  {
    path: '/space_upgrade',
    element: <SpaceUpgrade />,
  },
  {
    path: '/word_share',
    element: <ShareWord />,
  },
  {
    path: '/expand_space',
    element: <ExpandSpace />,
  },

  // 生成绘本
  {
    path: '/generate_book',
    children: [
      {
        path: 'create',
        element: <GenerateBookCreate />,
      },
      {
        path: 'list',
        element: <ResourcesList />,
      },
    ],
  },
  {
    path: '/creator', // 创作者中心页面
    element: <CreatorCenter />,
  },
  {
    path: '/edit_words',
    element: <EditWords />,
  },
  {
    path: '/cooperative_promotion',
    element: <CooperativePromotion />,
  },
  {
    path: '/live_class_promotion',
    element: <LiveClassPromotion />,
  },
  {
    path: '/coco_live_class',
    element: <CocoLiveClass />,
  },
  {
    path: '/coco_grammar_course',
    element: <CocoGrammarCourse />,
  },
  {
    path: '/disclaimer',
    element: <Disclaimer />,
  },
  {
    path: '/video_play_page',
    element: <VideoPlayPage />,
  },
  {
    path: '/phonetic',
    element: <PhoneticPage />,
  },
  {
    path: '/phonetic/letter',
    element: <LetterPhoneticPage />,
  },
  {
    path: '/phonics',
    element: <PhonicsPage />,
  },
  {
    path: '/ai_chat',
    element: <AiChat />,
  },
  {
    path: '/leaderboard',
    element: <Leaderboard />,
  },
  {
    path: '/mock_test_report_landing',
    element: <MockTestReportLanding />,
  },
  {
    path: '/mindmap',
    element: <MindmapPage />,
  },
  {
    path: '/my_group_purchase',
    element: <MyGroupPurchase />,
  },
  {
    path: '/join_group_purchase',
    element: <JoinGroupPurchase />,
  },
  {
    path: '/group_share',
    element: <GroupPurchaseShareActivity />,
  },
]);

// 创建UserContext和Provider
// 创建一个Context
export const UserContext = createContext<DeerUser | undefined>(undefined);

const App = () => {
  const [user, setUser] = useState<DeerUser | undefined>();

  useEffect(() => {
    if (isApp()) {
      // 注册全局消息监听器
      const handleMessage = (event: MessageEvent) => {
        if (event.data && event.data.type === 'setUserInfo') {
          console.log('setUserInfo123:', event.data.token);
          localStorage.setItem('token', event.data.toke);
          if (event.data.user) {
            setUser(event.data.user);
            console.log('setUserInfo setUser:', event.data.user);
          }
        }
      };
      window.addEventListener('message', handleMessage);
      // 在应用初始化时调用Flutter的获取用户信息的接口
      JsBridge.getUserInfo({ sourceFrom: 'h5' });
    }
    // 清理事件监听器
    // return () => {
    //   window.removeEventListener('message', handleMessage);
    // };
  }, []);

  return (
    <React.StrictMode>
      <UserContext.Provider value={user}>
        <Suspense fallback={<div />}>
          <RouterProvider router={router} />
        </Suspense>
      </UserContext.Provider>
    </React.StrictMode>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(<App />);

//不要等比缩放
// initRem();
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
